const DEFAULT_CONFIG = {
  siteToken: '',
  authToken: '',
  endpoint: '',
  trackPath: 'api/v2/event/track',
  debug: false,
  consent: true,
  language: 'tr',
};

const state = {
  config: { ...DEFAULT_CONFIG },
  userData: {},
  utm: {},
  dedupe: new Map(),
};

function logDebug(...args) {
  if (state.config.debug) {
    // eslint-disable-next-line no-console
    console.log('[SoulAnalytics]', ...args);
  }
}

function stableStringify(value) {
  const seen = new WeakSet();
  const sorter = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

  function walk(v) {
    if (v == null) return v;
    const t = typeof v;
    if (t === 'number' || t === 'string' || t === 'boolean') return v;
    if (t === 'function' || t === 'symbol') return undefined;
    if (v instanceof Date) return v.toISOString();
    if (Array.isArray(v)) return v.map(walk);
    if (t === 'object') {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
      const out = {};
      Object.keys(v).sort(sorter).forEach((k) => {
        const next = walk(v[k]);
        if (next !== undefined) out[k] = next;
      });
      return out;
    }
    return String(v);
  }

  try {
    return JSON.stringify(walk(value));
  } catch (e) {
    try {
      return JSON.stringify(String(value));
    } catch (err) {
      return '';
    }
  }
}

function hashString(str) {
  // djb2
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h) + str.charCodeAt(i);
    h &= 0xffffffff;
  }
  return (h >>> 0).toString(16);
}

function shouldDedupe(payload, windowMs = 1500) {
  try {
    const now = Date.now();
    const event = payload?.event || {};
    const keyObj = {
      siteToken: payload?.siteToken,
      visitorId: payload?.visitorId,
      sessionId: payload?.sessionId,
      userId: payload?.userId,
      pagePath: payload?.pagePath,
      eventName: event?.eventName,
      event: event,
    };
    // timestamp gibi uçucu alanları dedupe key'e katmayalım
    if (keyObj.event && typeof keyObj.event === 'object') {
      const e2 = { ...keyObj.event };
      delete e2.timestamp;
      delete e2.eventCallback;
      delete e2.event_callback;
      keyObj.event = e2;
    }

    const key = hashString(stableStringify(keyObj));
    const prev = state.dedupe.get(key);
    if (prev && (now - prev) < windowMs) {
      return true;
    }

    state.dedupe.set(key, now);
    if (state.dedupe.size > 500) {
      for (const [k, ts] of state.dedupe.entries()) {
        if ((now - ts) > (windowMs * 10)) state.dedupe.delete(k);
      }
    }
    return false;
  } catch (e) {
    return false;
  }
}

function normalizeEndpoint(endpoint) {
  if (!endpoint) {
    return '';
  }
  return String(endpoint).replace(/\/+$/, '');
}

function buildUrl(path) {
  const endpoint = normalizeEndpoint(state.config.endpoint);
  const cleanPath = String(path || '').replace(/^\/+/, '');
  if (!endpoint) {
    return `/${cleanPath}`;
  }
  return `${endpoint}/${cleanPath}`;
}

function safePrice(val, decimals = 2) {
  const n = Number(val);
  return (Number.isNaN(n) ? 0 : n).toFixed(decimals);
}

function normalizeProductCode(val) {
  if (val == null || val === '') {
    return undefined;
  }
  const s = String(val).replace(/^0+/, '') || '0';
  return s.padStart(15, '0');
}

function uuidv4() {
  // RFC4122 v4 - best-effort (no crypto dependency)
  try {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const hex = Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (e) {}
  const r = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${r()}${r()}-${r()}-${r()}-${r()}-${r()}${r()}${r()}`;
}

function safeGetLocation() {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    return window.location;
  } catch (e) {
    return null;
  }
}

function parseBrowserAndOs() {
  try {
    if (typeof navigator === 'undefined') return {};
    const ua = String(navigator.userAgent || '');
    const platform = String(navigator.platform || '');

    let browser = 'Unknown';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
    else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';

    let os = 'Unknown';
    if (/Mac/.test(platform) || /Mac OS X/.test(ua)) os = 'macOS';
    else if (/Win/.test(platform) || /Windows/.test(ua)) os = 'Windows';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Linux/.test(platform)) os = 'Linux';

    return { browser, os, userAgent: ua };
  } catch (e) {
    return {};
  }
}

function getOrCreateVisitorId() {
  try {
    const k = '_soul_visitor_id';
    const existing = localStorage.getItem(k);
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem(k, id);
    return id;
  } catch (e) {
    return uuidv4();
  }
}

function getOrCreateSessionId() {
  try {
    const k = '_soul_session_id';
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const id = uuidv4();
    sessionStorage.setItem(k, id);
    return id;
  } catch (e) {
    return uuidv4();
  }
}

function getLastPageUrl() {
  try {
    return sessionStorage.getItem('_soul_last_page_url') || '';
  } catch (e) {
    return '';
  }
}

function setLastPageUrl(url) {
  try {
    if (url) sessionStorage.setItem('_soul_last_page_url', String(url));
  } catch (e) {}
}

function buildTrackPayload(event = {}) {
  const loc = safeGetLocation();
  const { os, userAgent } = parseBrowserAndOs();

  const documentRef = (typeof document !== 'undefined' && document.referrer) ? document.referrer : '';
  const lastUrl = getLastPageUrl();
  // SPA'de document.referrer güncellenmez; bu yüzden bir önceki route'u sessionStorage'dan taşırız.
  const referrer = documentRef || lastUrl || (loc ? `${loc.origin}/` : '');
  let referrerHost = null;
  try {
    referrerHost = referrer ? (new URL(referrer)).host : null;
  } catch (e) {
    referrerHost = null;
  }

  const screenObj = (typeof window !== 'undefined' && window.screen) ? window.screen : null;
  const screenResolution = (screenObj && screenObj.width && screenObj.height)
      ? `${screenObj.width}x${screenObj.height}`
      : null;
  const viewportSize = (typeof window !== 'undefined' && window.innerWidth && window.innerHeight)
      ? `${window.innerWidth}x${window.innerHeight}`
      : null;

  const utm = state.utm || {};
  const utmSource = utm.utmSource ?? utm.utm_source ?? null;
  const utmMedium = utm.utmMedium ?? utm.utm_medium ?? null;
  const utmCampaign = utm.utmCampaign ?? utm.utm_campaign ?? null;

  const userId = state.userData?.userId ? Number(state.userData.userId) : 0;

  const normalizedEvent = {
    ...event,
    eventName: event.eventName || event.event,
    event: event.event || event.eventName,
  };

  const payload = {
    siteToken: state.config.siteToken || null,
    visitorId: getOrCreateVisitorId(),
    sessionId: getOrCreateSessionId(),
    userId: Number.isFinite(userId) ? userId : 0,
    firstName: state.userData?.firstName ?? null,
    lastName: state.userData?.lastName ?? null,
    gender: state.userData?.gender ?? null,
    birthday: state.userData?.birthday ?? null,
    phone: state.userData?.phone ?? null,
    email: state.userData?.email ?? null,
    latitude: state.userData?.latitude ?? null,
    longitude: state.userData?.longitude ?? null,
    pagePath: loc ? loc.pathname : null,
    pageTitle: (typeof document !== 'undefined' && document.title) ? document.title : null,
    pageUrl: loc ? loc.href : null,
    referrer,
    channelType: 'web',
    referrerHost,
    language: state.config.language || 'tr',
    screenResolution,
    viewportSize,
    userAgent: userAgent || null,
    timestamp: new Date().toISOString(),
    utmSource,
    utmMedium,
    utmCampaign,
    os: os || null,
    event: normalizedEvent,
  };

  // Son sayfayı bir sonraki event için referrer olarak sakla.
  if (loc && loc.href) {
    setLastPageUrl(loc.href);
  }

  return payload;
}

function request(path, method = 'GET', body = null) {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (state.config.authToken) {
    headers.Authorization = `Bearer ${state.config.authToken}`;
  }

  return fetch(buildUrl(path), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (response) => {
    const text = await response.text();
    let json;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (e) {
      json = { raw: text };
    }

    if (!response.ok) {
      const error = new Error('Soul analytics request failed');
      error.response = response;
      error.payload = json;
      throw error;
    }

    return json;
  });
}

export const SoulAnalytics = {
  configure(config = {}) {
    // Backward compatible: eski `token` authToken olarak kabul edilir.
    const authToken = config.authToken || config.token || '';
    state.config = {
      ...state.config,
      ...config,
      authToken,
      endpoint: normalizeEndpoint(config.endpoint || state.config.endpoint),
    };

    if (config.language) {
      this.setLanguage(config.language);
    }

    if (typeof config.consent === 'boolean') {
      this.setConsent(config.consent);
    }

    logDebug('configured', state.config);
  },

  setUserData(userData = {}) {
    state.userData = {
      ...state.userData,
      ...userData,
    };

    if (userData.latitude != null && userData.latitude !== '') {
      sessionStorage.setItem('_soul_latitude', String(userData.latitude));
    }

    if (userData.longitude != null && userData.longitude !== '') {
      sessionStorage.setItem('_soul_longitude', String(userData.longitude));
    }

    logDebug('setUserData', state.userData);
  },

  setConsent(consent) {
    state.config.consent = !!consent;
    logDebug('setConsent', state.config.consent);
  },

  setLanguage(language) {
    state.config.language = language || 'tr';
    logDebug('setLanguage', state.config.language);
  },

  setUtmParams(utm = {}) {
    state.utm = {
      ...state.utm,
      ...utm,
    };

    logDebug('setUtmParams', state.utm);
  },

  track(event = {}) {
    if (!state.config.consent) {
      logDebug('track skipped (consent=false)', event);
      return Promise.resolve({ skipped: true, reason: 'consent_false' });
    }

    if (!event || !event.eventName) {
      return Promise.reject(new Error('event.eventName is required'));
    }

    const payload = buildTrackPayload(event);

    if (shouldDedupe(payload)) {
      logDebug('track skipped (deduped)', payload);
      return Promise.resolve({ skipped: true, reason: 'deduped' });
    }

    logDebug('track', payload);
    return request(state.config.trackPath, 'POST', payload);
  },

  safePrice,
  normalizeProductCode,
};

export default SoulAnalytics;
